/**
 * RequestAchInfoPrompt — the "you can't pay online yet" prompt shown to users without
 * READ:ELECTRONIC-PAYMENT, with the Request ACH Information button and its confirm dialog.
 * Shared by Batches to Pay (full promo card) and the Payment Plans page (one-line banner)
 * so the request flow and its messages live in one place.
 *
 * @param {number|null} agencyId - agency the request is raised for; null shows an error toast
 * @param {'promo'|'banner'} layout
 */
import { useState } from 'react';
import { toast } from 'react-toastify';
import { OathButton, OathConfirmDialog } from '../../../shared/OathUI';
import { requestAchInfo } from '../../../../services/oathPowerReportService';

const RequestAchInfoPrompt = ({ agencyId, layout = 'promo' }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [requesting, setRequesting] = useState(false);

  const handleConfirm = async () => {
    if (!agencyId) {
      toast.error('No agency available for this request');
      setConfirmOpen(false);
      return;
    }
    setRequesting(true);
    const result = await requestAchInfo(agencyId);
    setRequesting(false);
    setConfirmOpen(false);
    if (result.success) toast.success(result.message);
    else toast.error(result.error);
  };

  const button = (
    <OathButton onClick={() => setConfirmOpen(true)} variant="success" size="sm">
      Request ACH Information
    </OathButton>
  );

  const dialog = (
    <OathConfirmDialog
      open={confirmOpen}
      title="Request ACH Information"
      message="This will notify our team to reach out to you regarding ACH payment setup. Would you like to continue?"
      confirmLabel={requesting ? 'Sending...' : 'Send Request'}
      cancelLabel="Cancel"
      variant="warning"
      onConfirm={handleConfirm}
      onCancel={() => setConfirmOpen(false)}
    />
  );

  if (layout === 'banner') {
    return (
      <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border-l-4 border-primary flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Online payments aren't enabled for your account. Contact us to set up ACH and pay your plans here.
        </p>
        {button}
        {dialog}
      </div>
    );
  }

  return (
    <div className="mb-6 p-5 bg-gray-50 dark:bg-gray-800/50 rounded-xl border-l-4 border-primary">
      <h3 className="text-base font-bold text-gray-900 dark:text-white mb-3">
        Settle your batches quickly and securely with ACH
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
        We offer ACH as a convenient way to remit payment for your execution reports and batches directly through the portal, eliminating the need to write and mail checks.
      </p>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Contact us to enable electronic payments on your account and start processing batch payments online.
      </p>
      <hr className="my-4 border-primary" />
      {button}
      {dialog}
    </div>
  );
};

export default RequestAchInfoPrompt;
